import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/auth";
import { CATEGORIES, eur } from "@/lib/constants";

const SUPPORTS = [
  "Carte", "Flyer", "Carte postale", "Marque-page", "Affiche",
  "Réglette", "Invitation", "Fiche", "Support pédagogique", "Format personnalisé",
];

export default function ServiceCategory() {
  const { category } = useParams();
  const [services, setServices] = useState([]);
  const cat = CATEGORIES.find((c) => c.slug === category);

  useEffect(() => {
    if (!cat) return;
    api.get(`/services?category=${category}`).then((r) => setServices(r.data));
  }, [category, cat]);

  if (!cat) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`page-category-${category}`}>
      <section className="cs-cream border-b border-black/10 py-20">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">{cat.eyebrow}</div>
          <h1 className="cs-h1 mt-4 text-5xl lg:text-7xl">{cat.label}</h1>
          <p className="mt-6 max-w-2xl text-neutral-700 cs-body text-[15px] sm:text-base">{cat.description}</p>
        </div>
      </section>

      <section className="cs-cream py-16">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <Link key={s.id} to={`/prestation/${s.slug}`} className="cs-card p-8 flex flex-col justify-between min-h-[280px]"
                data-testid={`cat-svc-${s.slug}`}>
                <div>
                  <h3 className="cs-h3 text-2xl">{s.name}</h3>
                  <p className="mt-3 text-[15px] text-neutral-600">{s.short_description}</p>
                  {s.tiers?.length > 0 ? (
                    <ul className="mt-3 space-y-1 text-[13px]">
                      {s.tiers.slice(0, 3).map((t, i) => (
                        <li key={i} className={`flex justify-between ${t.highlight ? "text-black font-medium" : "text-neutral-500"}`}>
                          <span>{t.label}</span>
                          <span className="font-mono">{t.note || `${t.price} €`}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    s.price_note && <p className="mt-3 text-[13px] font-mono text-neutral-500">{s.price_note}</p>
                  )}
                </div>
                <div className="mt-6 pt-4 border-t border-black/10 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">À partir de</div>
                    <div className="font-serif text-3xl">{eur(s.starting_price)}</div>
                  </div>
                  <ArrowUpRight size={20} />
                </div>
              </Link>
            ))}
          </div>

          {/* Exemples de supports — uniquement pour Création print */}
          {category === "creation" && (
            <div className="mt-16 border-t border-black/15 pt-10" data-testid="creation-supports">
              <div className="cs-eyebrow text-neutral-500">Exemples de supports couverts</div>
              <h2 className="cs-h3 mt-3 text-3xl lg:text-4xl">Ce que couvre la création d'un support imprimé</h2>
              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {SUPPORTS.map((s) => (
                  <div key={s} className="border border-black/15 bg-white px-4 py-4 text-center text-[15px] font-serif" data-testid={`support-${s}`}>
                    {s}
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[14px] text-neutral-600 max-w-2xl">
                Une seule prestation « Création d'un support imprimé » couvre l'ensemble de ces supports.
                Le tarif dépend de la complexité (simple 39 € · standard 59 € · complexe 79 €) et un verso simple
                est proposé en option (+19 €). <span className="text-black">Fichier final prêt à imprimer inclus</span>,
                ainsi que 2 séries de modifications mineures.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
